import React from 'react';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Extensible, type Styleable } from '../../variants';

/**
 * `Extensible` because an app may add a tone through `UIVariantsProvider`
 * without this union learning about it — the known four still autocomplete.
 */
export type CalloutTone = Extensible<'warning' | 'danger' | 'info' | 'neutral'>;

/**
 * Two axes: the tone, and whether the lead-in is long enough to force the text
 * -block layout. Declared here so an app can add a fifth tone, or reskin one,
 * through the override registry instead of forking the component.
 */
export const calloutRecipe = defineRecipe('callout', 'ui-callout', {
    variants: {
        tone: {
            warning: 'ui-callout--warning',
            danger: 'ui-callout--danger',
            info: 'ui-callout--info',
            neutral: 'ui-callout--neutral',
        },
        layout: { row: 'ui-callout--row', block: 'ui-callout--block' },
    },
    defaultVariants: { tone: 'neutral', layout: 'row' },
});

export interface CalloutProps extends Styleable {
    children: React.ReactNode;
    tone?: CalloutTone;
    /**
     * Bold lead-in before the message, e.g. "Chưa có ánh xạ".
     *
     * Pass `longLabel` when this runs past roughly 40% of the callout's width
     * — see the component doc for why the layout has to change at that point.
     */
    label?: React.ReactNode;
    /**
     * Render the lead-in inline inside the text block instead of as a
     * fixed-width flex child. Required for long labels.
     */
    longLabel?: boolean;
    icon?: React.ReactNode;
    /**
     * EAA-1817: controls that belong to this callout, rendered at its trailing
     * edge — "Hoàn tác" on an undo notice, "Đồng bộ danh mục · Thử lại" on a
     * failed write, "Bỏ liên kết · Lưu thay đổi" on frame 55's inline error row.
     *
     * The handoff draws a callout WITH buttons in it more often than without,
     * and this component had no slot for them, so callers hand-built the whole
     * banner instead of using the callout at all — which is how `/console/role-map`
     * ended up with a `.role-map-undo-banner` that shared none of the callout's
     * tone palette. Kept out of `children` so the sentence still owns the
     * flexible column and the buttons still sit on one line beside it.
     *
     * `flex: none` on the slot: an action must never be what wraps first.
     */
    actions?: React.ReactNode;
}

/**
 * EAA-1804: the sheet's inline callout — tinted fill, 1px accent border with a
 * 4px leading edge.
 *
 * TWO LAYOUTS, AND THE CHOICE IS NOT COSMETIC. A short lead-in renders as a
 * `flex` row with `flex: none; white-space: nowrap` on the label — without
 * those it collapses to a ~60px column and wraps mid-phrase. A *long* lead-in
 * must not be a flex row at all: `nowrap` on a long label reserves half the
 * box, squeezing the sentence into a ragged column that breaks every three or
 * four words. Past roughly 40% of the callout width the sentence column falls
 * under ~55% of the box and the text block layout is the only readable one —
 * the handoff measured a 214px label in a 440px callout (48.6%) as clearly
 * past the line.
 *
 * Callers pick via `longLabel` rather than this component measuring itself:
 * measuring would need a layout effect and a re-render, and the caller
 * already knows whether its label is a two-word tag or half a sentence.
 *
 * Ant Design's `Alert` is not used here because its `message`/`description`
 * split hardcodes the stacked layout — the inline lead-in that this component
 * exists to get right is not expressible through it.
 *
 * A `danger` callout should state the consequence, not just the condition
 * ("nothing is granted", not merely "mapping paused").
 */
export function Callout({
    children,
    tone = 'neutral',
    label,
    longLabel = false,
    icon,
    actions,
    className,
    style,
}: CalloutProps): React.ReactElement {
    const colors = useUIColors();
    const isBlock = Boolean(label && longLabel);
    // EAA-1839: this component was the one kit primitive that took neither
    // `className` nor `style`, against the barrel's own rule that every component
    // exposes both on its root. Callers therefore wrapped it in a positioning
    // `<div>` just to place it in a grid — which is exactly the defect that rule
    // exists to prevent. Merge order is the contract: recipe classes first, the
    // caller's after, so an equal-specificity page rule wins without `!important`.
    const recipeClassName = useRecipe(calloutRecipe, {
        tone,
        layout: isBlock ? 'block' : 'row',
    });

    const palettes: Record<string, { bg: string; border: string; fg: string }> = {
        warning: { bg: colors.warningBg, border: colors.warning, fg: colors.warningText },
        danger: { bg: colors.errorBg, border: colors.error, fg: colors.errorText },
        info: { bg: colors.infoBg, border: colors.infoBorder, fg: colors.info },
        // Neutral carries no accent edge — it is a note, not an alert.
        neutral: { bg: colors.fill, border: 'transparent', fg: colors.textSecondary },
    };
    // An app-defined tone has no inline palette here by design: it supplies its
    // own look through the class, and falling back to `neutral` keeps the box
    // legible rather than rendering it unstyled.
    const palette = palettes[tone] ?? palettes.neutral;
    const accented = tone !== 'neutral' && palettes[tone] !== undefined;

    const base: React.CSSProperties = {
        padding: '12px 14px',
        borderRadius: 8,
        background: palette.bg,
        border: accented ? `1px solid ${palette.border}` : 'none',
        borderLeftWidth: accented ? 4 : undefined,
        color: palette.fg,
        fontSize: accented ? 14 : 13,
        lineHeight: 1.55,
    };

    // Long lead-in: a normal text block, so the sentence continues on the same
    // line as the label and wraps at the full content width.
    if (isBlock) {
        return (
            <div
                role={tone === 'danger' ? 'alert' : undefined}
                className={cx(recipeClassName, className)}
                style={{ ...base, ...style }}
            >
                {icon && <span style={{ marginRight: 8 }}>{icon}</span>}
                <strong style={{ fontWeight: 700 }}>{label}</strong> <span>{children}</span>
                {actions && (
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 10,
                        }}
                    >
                        {actions}
                    </span>
                )}
            </div>
        );
    }

    // Short lead-in (or none): flex row. `flex: none` + `nowrap` on the label
    // are load-bearing — see the doc comment.
    return (
        <div
            role={tone === 'danger' ? 'alert' : undefined}
            className={cx(recipeClassName, className)}
            style={{ ...base, display: 'flex', gap: 10, alignItems: 'flex-start', ...style }}
        >
            {icon && <span style={{ flex: 'none' }}>{icon}</span>}
            {label && (
                <strong style={{ flex: 'none', whiteSpace: 'nowrap', fontWeight: 700 }}>
                    {label}
                </strong>
            )}
            <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
            {actions && (
                <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {actions}
                </span>
            )}
        </div>
    );
}

export default Callout;
